import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type CreateFleetDto,
  type CreateOrganizationDto,
  type CreatePaymentConfigurationDto,
  createFleet,
  createOrganization,
  createPaymentConfiguration,
  getFleetsByOrganization,
  getOrganizations,
  getPaymentConfigsByOrganization,
  type UpdateFleetDto,
  type UpdateOrganizationDto,
  type UpdatePaymentConfigurationDto,
  updateFleet,
  updateOrganization,
  updatePaymentConfiguration,
} from "@/apis/organization/organization-apis";
import { queryKeys } from "./keys";

export function useOrganizations(enabled = true) {
  return useQuery({
    queryKey: queryKeys.organizations.all,
    queryFn: getOrganizations,
    enabled,
  });
}

export function useCreateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateOrganizationDto) => createOrganization(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.all,
      });
    },
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateOrganizationDto }) =>
      updateOrganization(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.all,
      });
    },
  });
}

export function useFleetsByOrganization(organizationId: string | null) {
  return useQuery({
    queryKey: queryKeys.organizations.fleets(organizationId ?? ""),
    queryFn: () => getFleetsByOrganization(organizationId as string),
    enabled: organizationId !== null,
  });
}

export function useCreateFleet(organizationId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateFleetDto) =>
      createFleet(organizationId as string, dto),
    onSuccess: () => {
      if (organizationId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.organizations.fleets(organizationId),
        });
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.all,
      });
    },
  });
}

export function useUpdateFleet(organizationId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateFleetDto }) =>
      updateFleet(id, dto),
    onSuccess: () => {
      if (organizationId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.organizations.fleets(organizationId),
        });
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.all,
      });
    },
  });
}

export function usePaymentConfigsByOrganization(
  organizationId: string | null,
  enabled = true
) {
  return useQuery({
    queryKey: queryKeys.organizations.paymentConfigs(organizationId ?? ""),
    queryFn: () => getPaymentConfigsByOrganization(organizationId as string),
    enabled: enabled && organizationId !== null,
  });
}

export function useCreatePaymentConfiguration(organizationId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreatePaymentConfigurationDto) =>
      createPaymentConfiguration(organizationId as string, dto),
    onSuccess: () => {
      if (organizationId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.organizations.paymentConfigs(organizationId),
        });
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.all,
      });
    },
  });
}

export function useUpdatePaymentConfiguration(organizationId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      dto,
    }: {
      id: string;
      dto: UpdatePaymentConfigurationDto;
    }) => updatePaymentConfiguration(id, dto),
    onSuccess: () => {
      if (organizationId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.organizations.paymentConfigs(organizationId),
        });
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.all,
      });
    },
  });
}
